import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2 } from "lucide-react";
import { toast } from "sonner";
import { useDirtyGuard } from "../../context/DirtyGuard";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { Field } from "../../components/neu/Field";
import { stripLang } from "../../lib/langInput";
import { GeoCascade } from "../../components/geo/GeoCascade";
import { LogoUpload } from "../admin/branch/LogoUpload";
import { COMPANY_FIELDS as FIELDS, companyPayload } from "./companyFields";
import { myCompany, updateCompany } from "../../api/directory";
import { clearCompanyCache } from "../../api/useMyCompany";
import { CompanySerial } from "./CompanySerial";

export const CompanyProfile = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    myCompany()
      .then((data) => {
        setForm(data);
        setSaved(data);
      })
      .catch(() => setForm(null));
  }, []);

  const save = useCallback(async () => {
    setBusy(true);
    try {
      const fresh = await updateCompany(companyPayload(form));
      clearCompanyCache();
      setForm(fresh);
      setSaved(fresh);
      toast.success(t("company.saved"));
    } catch {
      toast.error(t("errors.generic"));
      throw new Error("save_failed");
    } finally {
      setBusy(false);
    }
  }, [form, t]);

  useDirtyGuard(Boolean(form && saved && JSON.stringify(form) !== JSON.stringify(saved)), save);

  if (!form) return null;

  return (
    <NeuCard data-testid="company-profile">
      <p className="label-xs flex items-center gap-2">
        <Building2 className="h-4 w-4 text-primary" /> {t("company.title")}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{t("company.subtitle")}</p>
      <CompanySerial serial={form.serial} className="mt-4" />
      <p className="mt-2 text-[11px] leading-6 text-muted-foreground">{t("company.serialHint")}</p>

      <div className="mt-5">
        <LogoUpload label={t("company.logo")} value={form.logo_url} onChange={(url) => setForm({ ...form, logo_url: url })} />
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {FIELDS.map(({ key, label, hint, icon, accent, type }) => (
          <div key={key} className={hint ? "sm:col-span-2" : ""}>
            <Field
              label={t(label)}
              testId={`company-${key}`}
              type={type || "text"}
              value={form[key] ?? ""}
              onChange={(v) => setForm({ ...form, [key]: key === "nickname" ? stripLang(v, "en") : v })}
              accent={accent}
              icon={icon}
            />
            {hint ? (
              <p className="mt-1 text-[11px] font-semibold text-primary" data-testid={`company-${key}-hint`}>
                {t(hint)}
              </p>
            ) : null}
          </div>
        ))}
      </div>

      <div className="mt-4">
        <p className="label-xs mb-2 flex items-center gap-1.5 text-primary">
          <Building2 className="h-3.5 w-3.5" /> {t("company.location")}
        </p>
        <GeoCascade prefix="company-geo" gps value={{ country: form.country, region: form.region, city: form.city, lat: form.lat, lng: form.lng }}
          onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} onAddress={(line) => setForm((f) => ({ ...f, address: line }))} />
      </div>

      <NeuButton variant="primary" className="mt-5" data-testid="company-save" disabled={busy} onClick={save}>
        {t("app.save")}
      </NeuButton>
    </NeuCard>
  );
};
